import React, { useState, useEffect } from 'react';
import { login, register, resetPassword, signInWithGoogle, signInWithMicrosoft, getOAuthRedirectResult } from '../firebase/auth';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

// The Super Admin / Settings "Copy Invite Link" button generates a URL like
// ?invite=<email>&company=<companyId>. The companyId is NEVER trusted here —
// trusting it would let anyone self-assign to any company just by editing
// the URL, which is exactly the cross-company leak this app already fixed
// once. We only use the invite param to PREFILL the email field, so the
// person signing up can't mistype or mis-case the address the admin actually
// pre-registered — actual authorization still comes from the server-created
// pre-registration stub looked up by (normalized) email in AuthContext.js.
const inviteEmailFromUrl = () => {
  try {
    return new URLSearchParams(window.location.search).get('invite') || '';
  } catch {
    return '';
  }
};

export default function Login() {
  const invitedEmail = inviteEmailFromUrl();
  const [mode, setMode]         = useState(invitedEmail ? 'signup' : 'login'); // 'login' | 'signup' | 'reset'
  const [email, setEmail]       = useState(invitedEmail);
  // Locked = field is read-only, showing exactly the email the admin invited.
  // This is the whole point of prefilling: it can't be mistyped or mis-cased
  // if the person never has to type it. They can still unlock it (e.g. they
  // were sent the wrong link), at which point it behaves like a normal field.
  const [emailLocked, setEmailLocked] = useState(!!invitedEmail);
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [resetSent, setResetSent] = useState(false);

  // ── White-label branding: loaded from publicBranding/{hostname} if on a custom domain ──
  const [orgBranding, setOrgBranding] = useState(null); // null = show Clarion defaults

  useEffect(() => {
    const hostname = window.location.hostname;
    const defaultHosts = ['localhost', 'crm.clarioneducation.in', '127.0.0.1'];
    if (defaultHosts.some(h => hostname === h || hostname.endsWith(h))) return;
    // On a custom domain — fetch the organisation's public branding
    getDoc(doc(db, 'publicBranding', hostname))
      .then(snap => {
        if (!snap.exists()) return;
        const data = snap.data();
        setOrgBranding(data);
        // ── Dynamically update browser tab icon + title ──────────────────────
        if (data.logoUrl) {
          // Replace all existing favicon link tags with the org's logo
          document.querySelectorAll("link[rel*='icon']").forEach(el => el.remove());
          const favicon = document.createElement('link');
          favicon.rel = 'icon';
          // logoUrl is a base64 data URL — browsers accept this directly as a favicon
          favicon.href = data.logoUrl;
          document.head.appendChild(favicon);
        }
        if (data.tabTitle || data.orgName) {
          document.title = data.tabTitle || data.orgName;
        }
      })
      .catch(() => {}); // silently fall back to Clarion branding on any error
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      // AuthContext will detect the auth state change → App.js shows CRM
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth redirect result on page load (Google / Microsoft redirect-based sign-in)
  useEffect(() => {
    setLoading(true);
    getOAuthRedirectResult()
      .then((result) => {
        // null = no pending redirect (user navigated here directly, not via OAuth)
        // non-null = sign-in succeeded; onAuthStateChanged in AuthContext handles the rest
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        if (!err) return;
        console.error('[OAuth Redirect Error]', err.code, err.message);
        const msg = friendlyError(err.code, err.message)
          || err.message
          || 'Sign-in failed. Please try again.';
        setError(msg);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle(); // popup — resolves when sign-in completes
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        // User closed the Google window before finishing — not an error
        setLoading(false);
        return;
      }
      console.error('[Google Sign-in Error]', err?.code, err?.message);
      const msg = friendlyError(err?.code, err?.message)
        || err?.message
        || 'Google sign-in failed. Please try again.';
      setError(msg);
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithMicrosoft(); // opens popup — resolves when sign-in completes
      // onAuthStateChanged in AuthContext handles navigation; component unmounts
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        // User dismissed the popup — not an error worth showing
        setLoading(false);
        return;
      }
      console.error('[Microsoft Sign-in Error]', err?.code, err?.message);
      const msg = friendlyError(err?.code, err?.message)
        || err?.message
        || 'Microsoft sign-in failed. Please try again.';
      setError(msg);
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your full name.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setError('');
    setLoading(true);
    try {
      await register(email, password, name.trim());
      // AuthContext picks up the new user automatically
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const friendlyError = (code, rawMessage) => {
    const map = {
      // Email / password
      'auth/user-not-found':        'No account found with this email.',
      'auth/wrong-password':        'Incorrect password. Try again.',
      'auth/invalid-email':         'Please enter a valid email address.',
      'auth/too-many-requests':     'Too many attempts. Please try again later.',
      'auth/invalid-credential':    'Invalid email or password.',
      'auth/email-already-in-use':  'An account with this email already exists. Sign in instead.',
      'auth/weak-password':         'Password must be at least 6 characters.',
      // OAuth / popup
      'auth/popup-blocked':         'Sign-in popup was blocked by your browser. Please allow popups for this site and try again.',
      'auth/popup-closed-by-user':  null, // silenced
      'auth/cancelled-popup-request': null, // silenced
      'auth/account-exists-with-different-credential':
        'An account with this email already exists using a different sign-in method (e.g. Google or email/password). Please use that method to sign in.',
      // Microsoft / Azure specific
      'auth/operation-not-allowed':
        'Microsoft sign-in is not enabled yet. Please contact the platform admin to enable it in Firebase Console.',
      'auth/invalid-oauth-client-id':
        'Microsoft OAuth is misconfigured (invalid client ID). Contact the platform admin.',
      'auth/unauthorized-domain':
        'This domain is not authorized for Microsoft sign-in. Contact the platform admin to add it in Firebase Console.',
      'auth/internal-error':
        'An internal error occurred during Microsoft sign-in. Please try email/password login instead, or contact support.',
    };
    const friendly = map[code];
    if (friendly === null) return null; // silenced error
    if (friendly) return friendly;
    // For unmapped codes, surface the code so it can be diagnosed
    return `Sign-in failed (${code || 'unknown'}). Please try email/password instead or contact support.`;
  };

  // Clarion logo — full brand lockup (icon + text) from /public/clarion-logo.png
  const ClarionLogo = ({ width = 180 }) => (
    <img
      src="/clarion-logo.png"
      alt="Clarion CRM"
      style={{ width, height: 'auto', display: 'block' }}
    />
  );

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* ── LEFT BRAND PANEL ── */}
      <div style={{
        flex: '0 0 42%',
        background: 'linear-gradient(155deg, #0f1b35 0%, #1a2f5e 40%, #0d2346 70%, #091428 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '48px 52px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative blobs */}
        <div style={{ position: 'absolute', top: -100, right: -100, width: 320, height: 320, borderRadius: '50%', background: 'rgba(37,99,235,0.18)', filter: 'blur(80px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -60, left: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(16,185,129,0.12)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '45%', left: '60%', width: 180, height: 180, borderRadius: '50%', background: 'rgba(245,158,11,0.08)', filter: 'blur(50px)', pointerEvents: 'none' }} />

        {/* Logo + brand */}
        <div style={{ position: 'relative', zIndex: 1, marginBottom: 52 }}>
          <div style={{ marginBottom: 10 }}>
            {orgBranding?.logoUrl
              ? <div style={{ display: 'inline-block', background: '#fff', borderRadius: 12, padding: '8px 14px' }}>
                  <img src={orgBranding.logoUrl} alt={orgBranding.orgName || 'Logo'} style={{ height: 44, width: 'auto', display: 'block' }} />
                </div>
              : <img src="/clarion-mark.png" alt="Clarion" style={{ height: 54, width: 'auto', display: 'block' }} />
            }
          </div>
          <div style={{ width: 48, height: 3, background: `linear-gradient(90deg, ${orgBranding?.primaryColor || '#2563EB'}, #10B981)`, borderRadius: 2, marginTop: 20 }} />
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 360 }}>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: '#ffffff', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.5px' }}>
            {orgBranding ? `Welcome to ${orgBranding.orgName}` : 'The smarter CRM for college admissions'}
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: 40 }}>
            Manage leads, track applications, schedule meetings and grow your admissions pipeline — all in one place.
          </p>

          {/* Feature list */}
          {[
            { icon: '🎯', text: 'Lead management & pipeline tracking' },
            { icon: '🏛️', text: 'University rankings & shortlisting' },
            { icon: '📄', text: 'Document collection & applications' },
            { icon: '📅', text: 'Meeting scheduler with Teams & Meet' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{f.icon}</div>
              <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>{f.text}</span>
            </div>
          ))}
        </div>

        <div style={{ position: 'relative', zIndex: 1, marginTop: 'auto', paddingTop: 40 }}>
          {orgBranding
            ? <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.3px' }}>Powered by <strong style={{ color: 'rgba(255,255,255,0.5)' }}>Clarion CRM</strong></p>
            : <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.3px' }}>© 2025 Clarion Education · All rights reserved</p>
          }
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div style={{
        flex: 1,
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 32px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle background pattern */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(37,99,235,0.05) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(16,185,129,0.05) 0%, transparent 50%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 400 }}>
          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
            {orgBranding?.logoUrl
              ? <img src={orgBranding.logoUrl} alt={orgBranding.orgName || 'Logo'} style={{ height: 52, width: 'auto', display: 'block' }} />
              : <ClarionLogo width={180} />
            }
          </div>

          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 6, letterSpacing: '-0.3px' }}>
              {mode === 'login' ? 'Welcome back' : mode === 'signup' ? 'Create your account' : 'Reset password'}
            </h1>
            <p style={{ fontSize: 14, color: '#64748b' }}>
              {mode === 'login' ? 'Sign in to your workspace' : mode === 'signup' ? 'Join your team on Clarion' : 'We\'ll send you a reset link'}
            </p>
          </div>

          {/* Card */}
          <div style={{
            background: '#ffffff',
            borderRadius: 20,
            padding: 32,
            boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.06)',
          }}>
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                  Email address
                </label>
                <input
                  type="email"
                  className="crm-input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{ width: '100%', height: 44, fontSize: 14 }}
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Password</label>
                  <button
                    type="button"
                    onClick={() => { setMode('reset'); setError(''); }}
                    style={{ background: 'none', border: 'none', fontSize: 12, color: '#7c3aed', cursor: 'pointer', fontWeight: 500, padding: 0 }}
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  className="crm-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{ width: '100%', height: 44, fontSize: 14 }}
                />
              </div>

              {error && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
                style={{ width: '100%', justifyContent: 'center', height: 46, fontSize: 15, marginTop: 20 }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}>
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    Signing in…
                  </span>
                ) : 'Sign in'}
              </button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px' }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.08)' }} />
                <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>or continue with</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.08)' }} />
              </div>

              {/* SSO Buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  style={{
                    flex: 1, height: 44, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 10,
                    background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 500, color: '#374151',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <svg width="18" height="18" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Google
                </button>

                <button
                  type="button"
                  onClick={handleMicrosoftSignIn}
                  disabled={loading}
                  style={{
                    flex: 1, height: 44, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 10,
                    background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 500, color: '#374151',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <svg width="18" height="18" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                  </svg>
                  Microsoft
                </button>
              </div>

              {/* Sign up link */}
              <p style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginTop: 20, marginBottom: 0 }}>
                New to Clarion?{' '}
                <button type="button" onClick={() => { setMode('signup'); setError(''); }}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: 0 }}>
                  Create an account
                </button>
              </p>
            </form>
          )}

          {mode === 'signup' && (
            <form onSubmit={handleSignUp}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Full Name</label>
                <input type="text" className="crm-input" placeholder="e.g. Priya Sharma" value={name}
                  onChange={e => setName(e.target.value)} required
                  style={{ width: '100%', height: 44, fontSize: 14 }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Email address</label>
                <input type="email" className="crm-input" placeholder="you@company.com" value={email}
                  onChange={e => setEmail(e.target.value)} required
                  readOnly={emailLocked}
                  style={{ width: '100%', height: 44, fontSize: 14, background: emailLocked ? '#f8fafc' : undefined, color: emailLocked ? '#475569' : undefined }} />
                {emailLocked && (
                  <button type="button" onClick={() => setEmailLocked(false)}
                    style={{ background: 'none', border: 'none', color: '#7c3aed', fontWeight: 600, cursor: 'pointer', fontSize: 12, padding: '6px 0 0', display: 'block' }}>
                    Not you? Use a different email
                  </button>
                )}
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Password</label>
                <input type="password" className="crm-input" placeholder="Min. 6 characters" value={password}
                  onChange={e => setPassword(e.target.value)} required
                  style={{ width: '100%', height: 44, fontSize: 14 }} />
              </div>

              <div style={{ padding: '10px 14px', background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 9, fontSize: 12, color: '#5b21b6', marginBottom: 16, lineHeight: 1.5 }}>
                {invitedEmail
                  ? <>💡 You were invited with <strong>{invitedEmail}</strong> — sign up with this exact email so your account links to the right company.</>
                  : <>💡 If your admin has already added your email in Settings, your role will be assigned automatically.</>}
              </div>

              {error && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
                  {error}
                </div>
              )}

              <button type="submit" className="btn-primary" disabled={loading}
                style={{ width: '100%', justifyContent: 'center', height: 46, fontSize: 15 }}>
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}>
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    Creating account…
                  </span>
                ) : 'Create Account'}
              </button>

              <p style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginTop: 16, marginBottom: 0 }}>
                Already have an account?{' '}
                <button type="button" onClick={() => { setMode('login'); setError(''); }}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: 0 }}>
                  Sign in
                </button>
              </p>
            </form>
          )}

          {mode === 'reset' && (
            <form onSubmit={handleReset}>
              {resetSent ? (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <div style={{ fontSize: 40, marginBottom: 14 }}>📬</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Check your inbox</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
                    We sent a password reset link to <strong>{email}</strong>
                  </div>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => { setMode('login'); setResetSent(false); }}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                      Your email address
                    </label>
                    <input
                      type="email"
                      className="crm-input"
                      placeholder="you@company.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      style={{ width: '100%', height: 44, fontSize: 14 }}
                    />
                  </div>

                  {error && (
                    <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                    style={{ width: '100%', justifyContent: 'center', height: 46, fontSize: 15 }}
                  >
                    {loading ? 'Sending…' : 'Send reset link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); }}
                    style={{ background: 'none', border: 'none', width: '100%', textAlign: 'center', marginTop: 14, fontSize: 13, color: '#64748b', cursor: 'pointer' }}
                  >
                    ← Back to sign in
                  </button>
                </>
              )}
            </form>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 20 }}>
          Secured by Clarion · Enterprise-grade protection
        </p>
      </div>
    </div>

    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
  );
}
